import {generatePath, useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {Grid, useMediaQuery} from "@mui/material";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {createApplication, deleteApplication, getApplication, updateApplication} from "./ApplicationsApi";
import {Application, defaultApplication} from "./Application";
import {ApplicationHeaderNavigation} from "./ApplicationHeaderNavigation";
import {ApplicationMenu, ApplicationSubPages} from "./ApplicationMenu";
import React, {useEffect, useState} from "react";
import {ApplicationContent} from "./ApplicationContent";
import {SubPagesProvider, useSubPages} from "../../shared/components/page/SubPageProvider";
import {Theme} from "@mui/system";
import {Breakpoint} from "@mui/material/styles";
import {APPLICATIONS_QUERY_KEY} from "./ApplicationProvider";
import {routePaths} from "../../shared/components/router/routes";

export const SMALL_SCREEN_BREAKPOINT: Breakpoint = "lg";
const APPLICATION_QUERY_KEY = 'application';

export function ApplicationPage() {
  const axios = useAxios();
  const {t} = useTranslation();
  const params = useParams();
  const smallScreens = useMediaQuery((theme: Theme) => theme.breakpoints.down(SMALL_SCREEN_BREAKPOINT));
  const applicationId = params.applicationId || "0";
  const [application, setApplication] = useState<Application>();

  const {data} = useQuery(APPLICATION_QUERY_KEY, () => getApplication(axios, applicationId), {enabled: applicationId !== "new"});

  useEffect(() => {
    if (data) {
      setApplication(data);
    }
  }, [data]);

  useEffect(() => {
    if (applicationId === "new") {
      setApplication(defaultApplication(t));
    }
  }, [applicationId, t]);

  if (!application) {
    return <></>;
  }

  return (
    <Grid container={true} spacing={2}>
      <SubPagesProvider defaultSubPage={(smallScreens && applicationId !== "new") ? undefined : ApplicationSubPages.INFORMATION}>
        <Grid item={true} xs={12}>
          <ApplicationHeaderNavigation application={application} />
        </Grid>
        <ApplicationPageContent application={application} />
      </SubPagesProvider>
    </Grid>
  )
}

function ApplicationPageContent(props: {
  application: Application
}) {
  const {application} = props;
  const axios = useAxios();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {selectedSubPage, selectSubPage} = useSubPages();
  const smallScreens = useMediaQuery((theme: Theme) => theme.breakpoints.down(SMALL_SCREEN_BREAKPOINT));
  // console.info("smallScreens, selectedSubPage", smallScreens, selectedSubPage);

  // Having no sub page selected means "show the menu", which only exists as a separate view on
  // small screens. When we grow into the wide layout we fall back to the information sub page so
  // the content column is not left empty. This only ever selects a sub page, it never clears one,
  // so shrinking the window keeps the current sub page mounted along with its unsaved input.
  useEffect(() => {
    if (!smallScreens && !selectedSubPage) {
      selectSubPage(ApplicationSubPages.INFORMATION, true);
    }
  }, [smallScreens, selectedSubPage, selectSubPage]);

  const updateApplicationMutation = useMutation((application: Application) => updateApplication(axios, application));
  const createApplicationMutation = useMutation((application: Application) => createApplication(axios, application));
  const deleteApplicationMutation = useMutation((application: Application) => deleteApplication(axios, application.id!!));

  function handleSave(application: Application) {
    if (application.id) {
      return updateApplicationMutation.mutateAsync(application, {
        onSuccess: () => {
          queryClient.invalidateQueries(APPLICATIONS_QUERY_KEY).then();
          queryClient.invalidateQueries(APPLICATION_QUERY_KEY).then();
        },
      });
    } else {
      return createApplicationMutation.mutateAsync(application, {
        onSuccess: (newApp) => {
          queryClient.invalidateQueries(APPLICATIONS_QUERY_KEY).then();
          navigate(generatePath(routePaths.applicationWithId, {applicationId: newApp.id}));
        },
      });
    }
  }

  async function handleDelete(application: Application) {
    if (!application.id) {
      return Promise.resolve()
    }
    try {
      deleteApplicationMutation.mutate(application, {
          onSuccess: () => {
            queryClient.invalidateQueries(APPLICATIONS_QUERY_KEY);
            navigate(routePaths.application);
          }
        }
      )
    } catch (error) {
      console.error('Could not delete entity', error);
    }
  }

  return (
    <>
      {(!smallScreens || !selectedSubPage) &&
        <Grid item={true} xl={3} lg={4} md={12} xs={12}>
          <ApplicationMenu application={application} />
        </Grid>
      }
      {(selectedSubPage) &&
        <Grid item={true} xl={9} lg={8} md={12} xs={12}>
          <ApplicationContent application={application} handleSave={handleSave} handleDelete={handleDelete} />
        </Grid>
      }
    </>
  )
}
